"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.KANBAN_L2_GROUP_MATCHER_NAMES = exports.KANBAN_L2_GROUPABLE_PROPERTY_TYPES = exports.TABLE_DEFAULT_HIDDEN_PROPERTY_TYPES = exports.KANBAN_DEFAULT_VISIBLE_SYSTEM_BODY_PROPERTY_TYPES = exports.KANBAN_SYSTEM_BODY_PROPERTY_TYPES = exports.KANBAN_DEFAULT_VISIBLE_BODY_PROPERTY_COUNT = exports.DATABASE_VIEW_MODES = void 0;
exports.isCanonicalKanbanL2PropertyShape = isCanonicalKanbanL2PropertyShape;
exports.isCanonicalTablePropertyVisible = isCanonicalTablePropertyVisible;
exports.createCanonicalTableViewData = createCanonicalTableViewData;
exports.orderCanonicalKanbanProperties = orderCanonicalKanbanProperties;
exports.getCanonicalKanbanVisiblePropertyIds = getCanonicalKanbanVisiblePropertyIds;
exports.createCanonicalKanbanViewData = createCanonicalKanbanViewData;
exports.createCanonicalCalendarViewData = createCanonicalCalendarViewData;
exports.createCanonicalGanttViewData = createCanonicalGanttViewData;
exports.DATABASE_VIEW_MODES = [
    'table',
    'kanban',
    'calendar',
    'gantt',
];
exports.KANBAN_DEFAULT_VISIBLE_BODY_PROPERTY_COUNT = 4;
exports.KANBAN_SYSTEM_BODY_PROPERTY_TYPES = new Set([
    'created-time',
    'created-by',
    'updated-time',
    'updated-by',
]);
exports.KANBAN_DEFAULT_VISIBLE_SYSTEM_BODY_PROPERTY_TYPES = new Set([
    'created-time',
    'created-by',
]);
exports.TABLE_DEFAULT_HIDDEN_PROPERTY_TYPES = new Set([
    'created-time',
    'created-by',
    'updated-time',
    'updated-by',
]);
exports.KANBAN_L2_GROUPABLE_PROPERTY_TYPES = new Set([
    'select',
    'checkbox',
    'date',
    'relation',
    'member',
]);
exports.KANBAN_L2_GROUP_MATCHER_NAMES = new Set([
    'select',
    'checkbox',
    'boolean',
    'date-relative',
    'date-day',
    'date-month',
    'date-quarter',
    'date-year',
    'relation',
    'member',
]);
function isCanonicalKanbanL2PropertyShape(input) {
    if (!exports.KANBAN_L2_GROUPABLE_PROPERTY_TYPES.has(input.type))
        return false;
    if (input.type === 'relation' || input.type === 'member') {
        return input.cardinality === 'one';
    }
    return true;
}
function isCanonicalTablePropertyVisible(propertyType, explicitHide) {
    if (explicitHide !== undefined)
        return !explicitHide;
    return !exports.TABLE_DEFAULT_HIDDEN_PROPERTY_TYPES.has(propertyType);
}
function createCanonicalTableViewData(properties) {
    return {
        mode: 'table',
        columns: [],
        filter: { type: 'group', op: 'and', conditions: [] },
        header: {
            titleColumn: properties.find(property => property.type === 'title')?.id,
            iconColumn: 'type',
        },
    };
}
function orderCanonicalKanbanProperties(properties) {
    const title = [];
    const body = [];
    const systemBody = [];
    properties.forEach(property => {
        if (property.type === 'title') {
            title.push(property);
        }
        else if (exports.KANBAN_SYSTEM_BODY_PROPERTY_TYPES.has(property.type)) {
            systemBody.push(property);
        }
        else {
            body.push(property);
        }
    });
    return [...title, ...body, ...systemBody];
}
function getCanonicalKanbanVisiblePropertyIds(orderedProperties) {
    const visibleBodyIds = orderedProperties
        .filter(property => property.type !== 'title' &&
        !exports.KANBAN_SYSTEM_BODY_PROPERTY_TYPES.has(property.type))
        .slice(0, exports.KANBAN_DEFAULT_VISIBLE_BODY_PROPERTY_COUNT)
        .map(property => property.id);
    return new Set([
        ...orderedProperties
            .filter(property => property.type === 'title' ||
            exports.KANBAN_DEFAULT_VISIBLE_SYSTEM_BODY_PROPERTY_TYPES.has(property.type))
            .map(property => property.id),
        ...visibleBodyIds,
    ]);
}
function createCanonicalKanbanViewData(input) {
    const ordered = orderCanonicalKanbanProperties(input.properties);
    const visibleIds = getCanonicalKanbanVisiblePropertyIds(ordered);
    return {
        columns: ordered.map(property => ({
            id: property.id,
            hide: !visibleIds.has(property.id),
        })),
        filter: { type: 'group', op: 'and', conditions: [] },
        groupBy: input.groupBy,
        header: {
            titleColumn: ordered.find(property => property.type === 'title')?.id,
        },
        groupProperties: [],
        groupProperties2: [],
        groupCollapsed2: [],
        columnWidth: 260,
    };
}
/** A checkbox named like "All day" or "Ganztag" marks all-day events. */
const ALL_DAY_PROPERTY_NAME = /\b(all.?day|ganztag)\b/i;
/**
 * The data of a new calendar view. Start and end are the date columns the
 * caller resolved; a missing one is set up when the view is first opened.
 */
function createCanonicalCalendarViewData(input) {
    const columns = input.properties.map(property => property.id);
    for (const id of [input.startDateCol, input.endDateCol]) {
        if (id && !columns.includes(id))
            columns.push(id);
    }
    return {
        columns: columns.map(id => ({ id })),
        filter: { type: 'group', op: 'and', conditions: [] },
        header: {
            titleColumn: input.properties.find(property => property.type === 'title')
                ?.id,
            iconColumn: 'type',
        },
        startDateCol: input.startDateCol,
        endDateCol: input.endDateCol,
        allDayCol: input.properties.find(property => property.type === 'checkbox' &&
            ALL_DAY_PROPERTY_NAME.test(property.name))?.id,
        calendarMode: 'week',
    };
}
/**
 * The data of a new gantt view. Start and end are the date columns the
 * caller resolved; without both the view opens its setup wizard.
 */
function createCanonicalGanttViewData(input) {
    const columns = input.properties.map(property => property.id);
    for (const id of [input.startDateCol, input.endDateCol]) {
        if (id && !columns.includes(id))
            columns.push(id);
    }
    return {
        columns: columns.map(id => ({ id })),
        filter: { type: 'group', op: 'and', conditions: [] },
        header: {
            titleColumn: input.properties.find(property => property.type === 'title')
                ?.id,
        },
        startDateCol: input.startDateCol,
        endDateCol: input.endDateCol,
        zoomLevel: 'week',
        snapMode: 'day',
    };
}
